import { QuestionnaireCategoryItemsItem } from '@/api/generated-catch-backend/model';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export const RESOURCE_RISK_GRID = 'risk_grid.json';
export const RESOURCE_SECTION_DOTS = '/rau/section_dots.json';

const fetchLottieData = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch Lottie animation: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const useLottieData = (url: string | null) => {
  return useQuery({
    queryKey: ['lottie', url],
    queryFn: () => fetchLottieData(url!),
    enabled: !!url,
    staleTime: Infinity, // Never expire since graphics don't change frequently
    gcTime: Infinity, // Keep in memory indefinitely (formerly cacheTime)
  });
};

// Hook to prefetch multiple Lottie animations
export const usePrefetchLotties = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (urls: string[]) => {
      urls.forEach((url) => {
        queryClient.prefetchQuery({
          queryKey: ['lottie', url],
          queryFn: () => fetchLottieData(url),
          staleTime: Infinity,
          gcTime: Infinity,
        });
      });
    },
    [queryClient]
  );
};

// Utility function to extract all Lottie URLs from questionnaire data
export const extractLottieUrls = (questions: QuestionnaireCategoryItemsItem[]): string[] => {
  const interstitials = questions.filter(
    (q): q is QuestionnaireCategoryItemsItem & { questionnaireItemKind: 'interstitial' } =>
      q.questionnaireItemKind === 'interstitial',
  );

  const urls: string[] = interstitials
    .flatMap((interstitial) => {
      switch (interstitial.type) {
        case 'intro':
        case 'outro': {
          return (
            interstitial.frames
              ?.map((frame) => frame.graphic?.assetUrl)
              .filter((asset): asset is string => typeof asset === 'string') || []
          );
        }
        case 'image-interstitial':
          return interstitial.imageOrGraph?.type === 'lottie' ? [interstitial.imageOrGraph.asset] : [];
        case 'video-drawer-interstitial':
          return interstitial.imageOrGraph?.type === 'lottie' ? [interstitial.imageOrGraph.asset] : [];
        case 'card-stack-interstitial':
          return interstitial.cards?.flatMap((card) => (card.image?.endsWith('.json') ? [card.image] : [])) || [];
        case 'animated-section-divider':
        case 'section-divider-interstitial':
        default:
          return [];
      }
    })
    .filter(Boolean);

  return [...new Set(urls)];
};
